import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { DataTablePagination } from './DataTable';

/**
 * Which page of a server-paged list is open — the other half of what such a caller
 * needs before the table exists (`autoFitRows` is the first half: how many rows fit).
 *
 * It lives in the URL, so a reload lands where you were, Back out of a task returns to
 * the page you opened it from, and "row 40-something of the overdue jobs" is a link you
 * can send someone. Absent means the first page, so an untouched list keeps a clean URL
 * and the links built by hand elsewhere in the app stay correct.
 *
 * One param name for every list, because no route shows two paged lists at once.
 */
export const PAGE_PARAM = 'page';

/** A page number is a counting number; anything else in the URL reads as the first page. */
export const readPageParam = (params: URLSearchParams): number => {
  const page = Number(params.get(PAGE_PARAM));
  return Number.isInteger(page) && page > 0 ? page : 1;
};

/** Page 1 states nothing — see `PAGE_PARAM`. */
export const writePageParam = (params: URLSearchParams, page: number): void => {
  if (page > 1) params.set(PAGE_PARAM, String(page));
  else params.delete(PAGE_PARAM);
};

/**
 * `replace` is for a page nobody asked for — a correction, or a reset that rides along
 * with a filter change — so that Back still undoes exactly one step the reader took.
 */
export type SetPage = (page: number, options?: { replace?: boolean }) => void;

/** The page param, read and written. */
export const usePageParam = (): { page: number; setPage: SetPage } => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * A page turn is a step the reader took, so by default it goes into history the way a
   * filter change does: Back returns to the page you were reading rather than leaving
   * the list.
   */
  const setPage = useCallback<SetPage>(
    (next, options) => {
      setSearchParams(
        // The updater form rather than a copy taken at render time, so a page written in
        // the same tick as a filter — or by an effect a commit behind — cannot be undone
        // by whichever of the two lands second.
        (prev) => {
          const params = new URLSearchParams(prev);
          writePageParam(params, next);
          return params;
        },
        { replace: options?.replace },
      );
    },
    [setSearchParams],
  );

  return { page: readPageParam(searchParams), setPage };
};

type ClampedPageOptions = {
  /** What the URL asks for, and what was therefore asked of the service. */
  page: number;
  setPage: SetPage;
  /**
   * Rows matching the filter across every page, as the service counts them —
   * `undefined` until it has answered.
   */
  count: number | undefined;
  pageSize: number;
};

type ClampedPage = {
  /** The page that can actually be shown — `page`, unless it has run off the end. */
  currentPage: number;
  /**
   * Whether the two disagree, which lasts one commit. Worth a loader: the answer in hand
   * still describes the page that no longer exists, so its empty rows would read as
   * "nothing matches" until the corrected page arrives.
   */
  isCorrectingPage: boolean;
  /** The footer, as `DataTable` wants it. */
  pagination: DataTablePagination;
};

/**
 * The page a server-paged list can actually show, and the footer that states it.
 *
 * Read the param, ask the service for that page, then hand the answer's `count` back
 * here — the two steps are separate because each needs the other's output: the request
 * needs the page number, and the clamp needs the count that request came back with.
 *
 * A delete, or a resize that fits more rows, can leave the list shorter than the page
 * being looked at — falling back to the last page that exists beats an empty table that
 * reads as "nothing matches". Only once the service has answered, though: `count` is
 * unknown until then, and clamping against a zero would send an arriving `?page=3` back
 * to the first page before its rows had even been asked for. `count` is the whole
 * filter's rather than the page's, so the answer to a page that no longer exists still
 * says which page does.
 *
 * The correction goes back to the URL, so the address bar and the next page turn both
 * start from the page actually on screen — from an effect, not during render, because
 * `setSearchParams` goes through `navigate`, which cannot be called while rendering.
 */
export const useClampedPage = ({
  page,
  setPage,
  count,
  pageSize,
}: ClampedPageOptions): ClampedPage => {
  const lastPage = count === undefined ? page : Math.max(1, Math.ceil(count / pageSize));
  const currentPage = Math.min(page, lastPage);
  const isCorrectingPage = currentPage !== page;

  useEffect(() => {
    if (isCorrectingPage) setPage(currentPage, { replace: true });
  }, [isCorrectingPage, currentPage, setPage]);

  const pagination = useMemo<DataTablePagination>(
    () => ({ page: currentPage, pageSize, totalCount: count ?? 0, onPageChange: setPage }),
    [currentPage, pageSize, count, setPage],
  );

  return { currentPage, isCorrectingPage, pagination };
};
